import React from "react";
import { X } from "lucide-react";
import { SelectMenu, type TimePeriodOption } from "./SelectMenu";
import { Button } from "./Button";
import { DateRangeInput } from "./DateRangeInput";
import type { ColumnMetadata } from "../types/imports";

const DATE_PRESETS: TimePeriodOption[] = [
  { id: "this-year", value: "this-year", text: "This Year" },
  { id: "this-quarter", value: "this-quarter", text: "This Quarter" },
  { id: "this-month", value: "this-month", text: "This Month" },
  { id: "last-year", value: "last-year", text: "Last Year" },
  { id: "last-quarter", value: "last-quarter", text: "Last Quarter" },
  { id: "last-month", value: "last-month", text: "Last Month" },
  { id: "after", value: "after", text: "On or after..." },
];

/** Metabase base_types that represent dates */
const DATE_BASE_TYPES = [
  "type/Date",
  "type/DateTime",
  "type/DateTimeWithLocalTZ",
  "type/DateTimeWithTZ",
  "type/DateTimeWithZoneOffset",
  "type/Temporal",
];

export function isDateColumn(col: ColumnMetadata): boolean {
  return DATE_BASE_TYPES.some((t) => col.base_type.includes(t));
}

interface DateRangeConfigProps {
  columns: ColumnMetadata[];
  dateColumn: string | null;
  datePreset: string | null;
  afterMonth: string | null;
  onDateColumnChange: (column: string) => void;
  onDatePresetChange: (preset: string) => void;
  onAfterMonthChange: (month: string) => void;
  onRemove: () => void;
}

export function DateRangeConfig({
  columns,
  dateColumn,
  datePreset,
  afterMonth,
  onDateColumnChange,
  onDatePresetChange,
  onAfterMonthChange,
  onRemove,
}: DateRangeConfigProps) {
  const dateColumns = columns.filter(isDateColumn);

  const columnOptions: TimePeriodOption[] = dateColumns.map((col) => ({
    id: col.name,
    value: col.name,
    text: col.display_name || col.name,
  }));

  const selectedColumn =
    columnOptions.find((o) => o.value === dateColumn) || columnOptions[0];
  const selectedPreset =
    DATE_PRESETS.find((o) => o.value === datePreset) || DATE_PRESETS[0];

  const isAfter = selectedPreset?.value === "after";

  return (
    <div className="rounded-md bg-bg2 p-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-[13px] text-fg2 font-medium">
          Date Filter
        </span>
        <Button
          variant="tertiary"
          size="xs"

          leadingIcon={<X size={14} />}
          onClick={onRemove}
        />
      </div>

      <div className="space-y-2">
        {/* Column selector */}
        <SelectMenu
          options={columnOptions}
          selectedOption={selectedColumn}
          onChange={(opt) => onDateColumnChange(opt.value)}
          size="sm"
          className="w-full"
        />

        {/* Date range preset */}
        <SelectMenu
          options={DATE_PRESETS}
          selectedOption={selectedPreset}
          onChange={(opt) => onDatePresetChange(opt.value)}
          size="sm"
          className="w-full"
        />
        {isAfter && (
          <DateRangeInput
            placeholder="Select date"
            value={afterMonth ? (() => {
              const [y, m, d] = afterMonth.split("-").map(Number);
              return new Date(y, m - 1, d || 1);
            })() : undefined}
            onChange={(date) => {
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, "0");
              const dd = String(date.getDate()).padStart(2, "0");
              onAfterMonthChange(`${yyyy}-${mm}-${dd}`);
            }}
            minDate={new Date(2020, 0, 1)}
            maxDate={new Date(2030, 11, 31)}
          />
        )}
      </div>
    </div>
  );
}
